import { d550_backgroundCheck } from '@/backend/features/contact-management/defaults/d550_background_check';
import { requireManageMembersAndAnySystemPermission } from '@/backend/utils';
import { accountEntityName, mapAccountSectionToAuditTag } from '@juvare/contact-management/backend/features/accounts/shared';
import { requireAnySystemPermission } from '@juvare/contact-management/backend/features/permissions/shared';
import { administratorAccountEmail } from '@juvare/credentialing/backend/constants';
import { AuthError, defineEntity } from '@juvare/platform-app/App';
import { AppState, UserState } from '@juvare/platform-sdk-react/types';
import { BackgroundCheckStatus } from '../../types';
import { BackgroundCheckColumns, BackgroundCheckData } from '../background-check';
import { BackgroundCheckHistoryColumns, BackgroundCheckHistoryData, backgroundCheckHistoryColumns } from './types';

export const backgroundCheckHistoryEntity = defineEntity<BackgroundCheckHistoryData, BackgroundCheckHistoryColumns, AppState, UserState>({
    version: 2,
    columns: backgroundCheckHistoryColumns,
    onMigrate: (params) => params.oldData,
    onTransformColumns: (context, params) => {
        return {
            accountId: params.data.accountId,
            type: params.data.type,
            prevStatus: params.data.prevStatus,
            nextStatus: params.data.nextStatus,
            checkResponseByRequestId: params.data.checkResponseByRequestId,
            message: params.data.message,
            isUsingQuota: params.data.isUsingQuota,
            note: params.data.note,
            createdAt: params.data.createdAt ?? params.created,
            withDocument: Boolean(params.data.document),
        };
    },
    onAuthGet: (appState, userState, response) => {
        if (requireAnySystemPermission(userState, 'view-full-background-check-history')) {
            return response;
        } else if (requireManageMembersAndAnySystemPermission(userState, 'view-partial-background-check-history')) {
            return { ...response, data: { ...response.data, resultXml: undefined, request: undefined } };
        } else {
            throw new AuthError('Unauthorized');
        }
    },
    onAuthList: (appState, userState, response) => {
        if (
            requireManageMembersAndAnySystemPermission(
                userState,
                'view-full-background-check-history',
                'view-partial-background-check-history',
            )
        ) {
            return response;
        } else {
            throw new AuthError('Unauthorized');
        }
    },
    onAuthPost: (appState, userState, params) => {
        // During client migration, we need to bypass standard authorization checks to manage data as needed.
        // This is only permitted for the administrator account.
        if (userState.email === administratorAccountEmail) {
            return params.action === 'delete' ? params.prevData : params.data;
        }

        if (!requireManageMembersAndAnySystemPermission(userState, 'manage-background-check-data')) {
            throw new AuthError('Unauthorized');
        }

        if (params.action === 'create' || params.action === 'update') {
            delete params.data.internalStatusChange;
            return params.data;
        }
        return;
    },
    onChangeSync: (context, params) => {
        const accountStorage = context.storage<BackgroundCheckData, BackgroundCheckColumns>(accountEntityName);

        if (params.action === 'create' && !params.data.internalStatusChange) {
            const handleStatusList: Array<BackgroundCheckStatus> = ['didNotConsent', 'formIncomplete', 'readyToSubmit'];
            const nextStatus = params.data.nextStatus;

            if (nextStatus && !handleStatusList.includes(nextStatus)) {
                accountStorage.post({
                    action: 'partial-update',
                    id: params.data.accountId,
                    data: {
                        internalBackgroundCheckChange: {
                            isManualChange: params.data.isManualChange,
                            type: params.data.type,
                            status: nextStatus,
                            updatedAt: Date.now(),
                        },
                    },
                    auditTags: [mapAccountSectionToAuditTag(d550_backgroundCheck)],
                });
            }
        }
    },
});
